import { DesktopDatePicker } from "@mui/lab";
import EventIcon from "@mui/icons-material/Event";
import { Autocomplete, Box, Card, CircularProgress, MenuItem, Switch, TextField, Typography } from "@mui/material";
import moment from "moment";
import { createKyb } from "api/services/clients/client-kyb";
import { getTaskCreateClientDropDown } from "api/services/clients/clients";
import DrawerWrapper from "components/DrawerWrapper";
import LoadingButton from "components/LoadingButton";
import UploadImage from "components/UploadImage";
import { snack } from "components/toast";
import { Permissions } from "data/permissons";
import { documentTypes } from "data/constants";
import useFileUpload from "hooks/useFileUpload";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { DialogProps, InputChangeType, ResType } from "types";
import { handleError } from "utils/handleError";
import { usePermissions } from "context/PermissionsProvider";
import { mulitipleClient, primaryclient } from "assets";
import VirtualizedListboxComponent from "views/billing/invoices/AddInvoice/VirtualizedList";
import { debounce } from "lodash";

interface StateProps {
    client: any;
    clientGroup: any;
    documentName: string;
    documentNumber: string;
    customName: string;
    storage: any;
    isExpiry: boolean;
    expiryDate: string;
}

const initialState: StateProps = {
    client: null,
    clientGroup: null,
    documentName: "",
    documentNumber: "",
    customName: "",
    storage: null,
    isExpiry: true,
    expiryDate: "",
};

function AddKYBOrg({ open, setOpen }: DialogProps) {
    const queryClient = useQueryClient();
    const { permissions } = usePermissions();
    const [state, setState] = useState<StateProps>(_.cloneDeep(initialState));
    const { handleFileUpload, deleteFile, deleteAwsFiles, uploading } = useFileUpload();
    const [load, setLoad] = useState(false);
    const fileDataRef = useRef<any>("");

    // Virtualized client dropdown state
    const [clientsList, setClientsList] = useState<any[]>([]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const listboxRef = useRef<HTMLUListElement | null>(null);
    const selectedClient = state.client || state.clientGroup;

    const { isLoading: clientsLoading, isFetching }: ResType = useQuery(
        ["clients", { search: debouncedSearch, page, status: ["ACTIVE", "INACTIVE"], limit: 10 }],
        getTaskCreateClientDropDown,
        {
            enabled:
                open &&
                (permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) ||
                    permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)),
            keepPreviousData: true,
            onSuccess: (data: any) => {
                const newClients = data?.data?.result || [];
                const isNewSearch = page === 0;
                const unique = Array.from(
                    new Map(
                        [...(!isNewSearch ? clientsList : []), ...newClients].map((item) => [item.id, item])
                    ).values()
                );
                setClientsList(unique);
                setTotalCount(data?.data?.totalCount ?? null);
            },
        }
    );

    const debouncedFn = useMemo(() => debounce((val: string) => setDebouncedSearch(val), 0), []);

    const handleInputChange = useCallback(
        (value: string, reason: string) => {
            if (reason === "input" || reason === "clear") {
                setSearch(value);
                debouncedFn(value);
                setState((prev) => ({ ...prev, client: null, clientGroup: null }));
                setPage(0);
                if (page !== 0) setClientsList([]);
            }
        },
        [debouncedFn, page]
    );

    const handleScroll = (event: React.SyntheticEvent) => {
        if (selectedClient) return;
        const node = event.currentTarget as HTMLUListElement;
        listboxRef.current = node;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 10 && !isFetching) {
            setPage((prev) => prev + 1);
        }
    };

    const handleClientChange = (_: any, option: any) => {
        if (!option) {
            setState((prev) => ({ ...prev, client: null, clientGroup: null }));
        } else if (option.type) {
            // It's a client group
            setState((prev) => ({ ...prev, client: null, clientGroup: option }));
        } else {
            setState((prev) => ({ ...prev, client: option, clientGroup: null }));
        }
    };

    useEffect(() => {
        if (!open) {
            setState(_.cloneDeep(initialState));
            setClientsList([]);
            setSearch("");
            setDebouncedSearch("");
            setPage(0);
        }
    }, [open]);

    const { mutate } = useMutation(createKyb, {
        onSuccess: () => {
            snack.success("License Management Document Added");
            setOpen(false);
            setState(_.cloneDeep(initialState));
            setLoad(false);
            queryClient.invalidateQueries("kyb-org-summary");
        },
        onError: (err: any) => {
            snack.error(handleError(err));
            if (fileDataRef.current?.fileId) deleteFile(fileDataRef.current.fileId);
            else if (fileDataRef.current?.file) deleteAwsFiles(fileDataRef.current.file);
            setLoad(false);
        },
    });

    const handleChange = (e: InputChangeType) => {
        setState({ ...state, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setLoad(true);

        if (state.documentName === "custom" && state.customName.trim().length < 3) {
            setLoad(false);
            return snack.error("Please enter a valid document name (min 3 characters)");
        }
        if (state.documentNumber.trim().length < 3) {
            setLoad(false);
            return snack.error("Please enter a valid document number (min 3 characters)");
        }
        if (!state.expiryDate) {
            setLoad(false);
            return snack.error("Expiry Date is Requried");
        }

        const apiData: any = { ...state };
        apiData.documentName = apiData.documentName === "custom" ? apiData.customName : apiData.documentName;

        if (state.storage instanceof FormData) {
            const filedata = await handleFileUpload(state.storage);
            apiData.storage = { ...filedata, type: state.clientGroup ? "clientGroup" : state.client ? "client" : "organization" };
            fileDataRef.current = filedata;
        }

        mutate({
            ...apiData,
            client: state.client?.id || null,
            clientGroup: state.clientGroup?.id || null,
        });
    };

    return (
        <DrawerWrapper
            open={open}
            setOpen={() => { setOpen(false); setState(_.cloneDeep(initialState)); }}
            title="Add Document"
        >
            <form onSubmit={handleSubmit}>
                {/* Client / Client Group */}
                <Box mb={2.5}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                        Client / Client Group{" "}
                    </Typography>
                    <Autocomplete
                        clearOnEscape
                        clearOnBlur={false}
                        options={clientsList}
                        getOptionLabel={(o) => o?.displayName || ""}
                        isOptionEqualToValue={(o, v) => o?.id === v?.id}
                        loading={clientsLoading}
                        loadingText="Loading..."
                        value={clientsList.find((c) => c.id === selectedClient?.id) || null}
                        onInputChange={(_, val, reason) => handleInputChange(val, reason)}
                        onChange={handleClientChange}
                        ListboxComponent={VirtualizedListboxComponent}
                        ListboxProps={{
                            onScroll: handleScroll,
                            style: { scrollbarWidth: "none", msOverflowStyle: "none" },
                        }}
                        disableListWrap
                        noOptionsText={
                            clientsLoading || isFetching ? <CircularProgress size={24} /> : "No options"
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                fullWidth
                                placeholder="Search client or group..."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {clientsLoading || isFetching ? (
                                                <CircularProgress color="inherit" size={18} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} style={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography sx={{ display: "flex", alignItems: "center" }}>
                                    {option.displayName}
                                    {option.isPrimary && (
                                        <img src={primaryclient} width="22px" style={{ marginLeft: 8 }} alt="primary" />
                                    )}
                                    {option.type && (
                                        <img src={mulitipleClient} width="22px" style={{ marginLeft: 8 }} alt="group" />
                                    )}
                                </Typography>
                            </li>
                        )}
                    />
                </Box>

                {/* Document Type */}
                <Box mb={2.5}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                        Document Type <Typography component="span" color="error">*</Typography>
                    </Typography>
                    <TextField
                        name="documentName"
                        value={state.documentName}
                        onChange={handleChange}
                        size="small"
                        select
                        fullWidth
                        required
                    >
                        <MenuItem value="">— Select document type —</MenuItem>
                        {documentTypes.map((opt, i) => (
                            <MenuItem key={i} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                {/* Custom Document Name */}
                {state.documentName === "custom" && (
                    <Box mb={2.5}>
                        <Typography variant="body2" fontWeight={500} mb={0.5}>
                            Document Name <Typography component="span" color="error">*</Typography>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            name="customName"
                            value={state.customName}
                            onChange={handleChange}
                            inputProps={{ minLength: 3, maxLength: 50 }}
                        />
                    </Box>
                )}

                {/* Document ID */}
                <Box mb={2.5}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                        Document ID / Reference Number <Typography component="span" color="error">*</Typography>
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        name="documentNumber"
                        value={state.documentNumber}
                        onChange={handleChange}
                        placeholder="e.g. 11223344556677"
                        required
                        inputProps={{ minLength: 3, maxLength: 50 }}
                    />
                </Box>


                {/* Expiry Date */}

                <Box mb={2.5}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>
                        Expiry Date <Typography component="span" color="error">*</Typography>
                    </Typography>
                    <DesktopDatePicker
                        inputFormat="dd-MM-yyyy"
                        value={state.expiryDate ? new Date(state.expiryDate) : null}
                        onChange={(val: Date | null) => {
                            setState({ ...state, expiryDate: val ? moment(val).format("YYYY-MM-DD") : "" });
                        }}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth size="small" required={state.isExpiry} />
                        )}
                    />
                </Box>

                {/* Attachment */}
                <Box mb={3}>
                    <Typography variant="body2" fontWeight={500} mb={0.5}>Attachment</Typography>
                    <UploadImage
                        type={state.clientGroup ? "clientGroup" : state.client ? "client" : "organization"}
                        client={state.client?.id || state.clientGroup?.id || undefined}
                        name="image"
                        label="Click or drag file to upload"
                        onChange={(name: string, fileData: FormData) => {
                            fileData.append("kyb", "true");
                            setState({ ...state, storage: fileData });
                        }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        PDF, JPG, PNG · Max 10 MB
                    </Typography>
                </Box>

                {/* Submit */}
                <LoadingButton
                    loading={uploading || load}
                    disabled={uploading || load}
                    type="submit"
                    fullWidth
                    loadingColor="white"
                    title={uploading ? "Uploading..." : load ? "Saving..." : "Save Document"}
                    color="secondary"
                />
            </form>
        </DrawerWrapper>
    );
}

export default AddKYBOrg;
